/* XP, levels, streaks, badges, lesson completion and per-exercise results. */
import { toast } from '../toast';
import { dateKey } from '../util';
import { getProgress, type LevelState, updateProgress } from './state';

export type ModuleId = 'gitar' | 'nota' | 'vokal' | 'akor';
export const MODULE_IDS: readonly ModuleId[] = ['gitar', 'nota', 'vokal', 'akor'];

const LEVELS: readonly [number, string][] = [
  [0, 'Çırak'],
  [150, 'Kulak Kabartan'],
  [450, 'Dinleyici'],
  [900, 'Sokak Müzisyeni'],
  [1600, 'Sahne Müzisyeni'],
  [2800, 'Kulak Ustası'],
  [4500, 'Virtüöz'],
  [7000, 'Efsane'],
];

export interface LevelInfo {
  n: number;
  name: string;
  from: number;
  to: number;
  frac: number;
  next: string | null;
}

export function levelInfo(xp: number = getProgress().xp): LevelInfo {
  let i = 0;
  while (i + 1 < LEVELS.length && xp >= (LEVELS[i + 1] as [number, string])[0]) i++;
  const cur = LEVELS[i] as [number, string];
  const next = LEVELS[i + 1];
  return {
    n: i + 1,
    name: cur[1],
    from: cur[0],
    to: next ? next[0] : cur[0],
    frac: next ? (xp - cur[0]) / (next[0] - cur[0]) : 1,
    next: next ? next[1] : null,
  };
}

/** Consecutive practice days ending today (or yesterday if today has no XP yet). */
export function streak(): number {
  const days = getProgress().days;
  const d = new Date();
  if (!days[dateKey(d)]) d.setDate(d.getDate() - 1);
  let n = 0;
  while (days[dateKey(d)]) {
    n++;
    d.setDate(d.getDate() - 1);
  }
  return n;
}

export const todayXp = (): number => getProgress().days[dateKey()] || 0;

export const BADGES = {
  ilk_ders: { name: 'İlk Ders', desc: 'İlk dersini bitirdin.' },
  ilk_antrenman: { name: 'İlk Antrenman', desc: 'İlk antrenman setini tamamladın.' },
  kusursuz: { name: 'Kusursuz', desc: 'Bir seti hiç hata yapmadan bitirdin.' },
  seri_3: { name: '3 Günlük Seri', desc: 'Üç gün üst üste çalıştın.' },
  seri_7: { name: 'Haftalık Seri', desc: 'Yedi gün üst üste çalıştın.' },
  seri_30: { name: 'Ayın Müzisyeni', desc: 'Otuz gün üst üste çalıştın.' },
  mikrofon: { name: 'Sahneye Hazır', desc: 'Mikrofonu ilk kez kullandın.' },
  akort: { name: 'Akort Ustası', desc: 'Gitarını akort aletiyle akort ettin.' },
  sarki: { name: 'Şarkı Çıkardın', desc: 'Bir şarkının bütün akorlarını kulaktan buldun.' },
  birlikte: { name: 'Baştan Sona', desc: 'Bir şarkıyı baştan sona akorlarla çaldın.' },
  jam_x8: { name: 'Sahne Senin', desc: "Jam'de gitarınla ×8 çarpana ulaştın." },
  aralik_testi: { name: 'Sesimi Tanıyorum', desc: 'Ses aralığı testini tamamladın.' },
  isinma: { name: 'Isınmış Ses', desc: 'Vokal ısınma rutinini bitirdin.' },
  hiz_20: { name: 'Hızlı Parmaklar', desc: '60 saniyede 20 notayı doğru çaldın.' },
  xp_1000: { name: 'Bin Puan', desc: 'Toplam 1000 XP topladın.' },
  dort_modul: { name: 'Çok Yönlü', desc: 'Dört bölümün hepsinde antrenman yaptın.' },
} as const satisfies Record<string, { name: string; desc: string }>;
export type BadgeId = keyof typeof BADGES;

export function award(id: BadgeId): boolean {
  if (!BADGES[id] || getProgress().badges[id]) return false;
  updateProgress(
    (p) => {
      p.badges[id] = Date.now();
    },
    { badge: id },
  );
  toast(`Rozet kazandın: ${BADGES[id].name}`, { tone: 'good', icon: 'award' });
  return true;
}

export function addXp(n: number, module?: string): void {
  if (!(n > 0)) return;
  const d = dateKey();
  const before = levelInfo().n;
  updateProgress(
    (p) => {
      p.xp += n;
      p.days[d] = (p.days[d] || 0) + n;
      if (module) p.modXp[module] = (p.modXp[module] || 0) + n;
    },
    { xp: n },
  );
  const st = streak();
  if (st >= 3) award('seri_3');
  if (st >= 7) award('seri_7');
  if (st >= 30) award('seri_30');
  const p = getProgress();
  if (p.xp >= 1000) award('xp_1000');
  if (MODULE_IDS.every((m) => (p.modXp[m] ?? 0) > 0)) award('dort_modul');
  const after = levelInfo();
  if (after.n > before) toast(`Yeni seviye: ${after.name}`, { tone: 'good', icon: 'star' });
}

export const isLessonDone = (module: string, id: string): boolean => !!getProgress().lessons[`${module}.${id}`];

/** Marks a lesson complete; returns false if it already was. */
export function lessonDone(module: string, id: string): boolean {
  const key = `${module}.${id}`;
  if (getProgress().lessons[key]) return false;
  updateProgress((p) => {
    p.lessons[key] = Date.now();
  });
  award('ilk_ders');
  addXp(15, module);
  return true;
}

export interface LevelResult {
  stars: number;
  acc: number;
  score: number;
}

export function saveLevel(key: string, result: LevelResult): { prev: LevelState; next: LevelState; newBest: boolean } {
  const prev = getProgress().levels[key] || { stars: 0, acc: 0, best: 0, plays: 0 };
  const next: LevelState = {
    stars: Math.max(prev.stars, result.stars || 0),
    acc: Math.max(prev.acc, result.acc || 0),
    best: Math.max(prev.best || 0, result.score || 0),
    plays: (prev.plays || 0) + 1,
    last: Date.now(),
  };
  updateProgress(
    (p) => {
      p.levels[key] = next;
    },
    { level: key },
  );
  return { prev, next, newBest: (result.score || 0) > (prev.best || 0) };
}

export const levelState = (key: string): LevelState | null => getProgress().levels[key] || null;

/** Stores a numeric personal record if it beats the previous one. */
export function saveRecord(key: string, value: number, lowerIsBetter = false): { prev?: number; better: boolean } {
  const prev = getProgress().records[key] as number | undefined;
  const better = prev == null || (lowerIsBetter ? value < prev : value > prev);
  if (better)
    updateProgress((p) => {
      p.records[key] = value;
    });
  return { prev, better };
}
